import type { AdSlot, SponsoredListing, Transaction } from '@shopgnd/shared';
import { getDataClient } from '@/lib/amplify-client';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyDataClient = any;

const nowIso = () => new Date().toISOString();

function mapTransaction(row: Record<string, unknown>): Transaction {
  const rawType = String(row.type);
  const type: Transaction['type'] =
    rawType.includes('boost') ? 'boost' : rawType.includes('subscription') ? 'subscription' : 'order';
  const rawStatus = String(row.status);
  const status: Transaction['status'] =
    rawStatus === 'failed' ? 'failed' : rawStatus === 'pending' ? 'pending' : 'succeeded';

  return {
    id: String(row.id),
    type,
    userId: String(row.userId),
    vendorId: row.vendorId ? String(row.vendorId) : undefined,
    amountCents: Number(row.amountCents),
    currency: String(row.currency ?? 'XCD'),
    stripeId: row.stripeId ? String(row.stripeId) : undefined,
    status,
    createdAt: String(row.createdAt ?? nowIso()),
  };
}

function mapAdSlot(row: Record<string, unknown>): AdSlot {
  return {
    id: String(row.id),
    placement: row.placement as AdSlot['placement'],
    label: String(row.label),
    active: Boolean(row.active),
    basePriceCents: Number(row.basePriceCents),
    currency: String(row.currency ?? 'XCD'),
  };
}

function mapSponsored(row: Record<string, unknown>): SponsoredListing {
  return {
    id: String(row.id),
    slotId: String(row.slotId),
    productId: row.productId ? String(row.productId) : undefined,
    classifiedId: row.classifiedId ? String(row.classifiedId) : undefined,
    vendorId: String(row.vendorId),
    startsAt: String(row.startsAt),
    endsAt: String(row.endsAt),
    amountPaidCents: Number(row.amountPaidCents),
  };
}

export async function listTransactionsLive(): Promise<Transaction[]> {
  try {
    const client = await getDataClient();
    const res = await client.models.Transaction.list();
    return ((res.data ?? []) as Record<string, unknown>[])
      .map(mapTransaction)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  } catch (e) {
    console.error('[ShopGND] listTransactionsLive failed', e);
    return [];
  }
}

export async function listAdSlotsLive(): Promise<AdSlot[]> {
  try {
    const client = await getDataClient();
    const res = await client.models.AdSlot.list();
    return ((res.data ?? []) as Record<string, unknown>[]).map(mapAdSlot);
  } catch (e) {
    console.error('[ShopGND] listAdSlotsLive failed', e);
    return [];
  }
}

export async function updateAdSlotLive(
  id: string,
  patch: Partial<Pick<AdSlot, 'label' | 'placement' | 'basePriceCents' | 'currency' | 'active'>>,
): Promise<AdSlot | null> {
  try {
    const client = await getDataClient();
    const res = await client.models.AdSlot.update({ id, ...patch });
    return res.data ? mapAdSlot(res.data as Record<string, unknown>) : null;
  } catch (e) {
    console.error('[ShopGND] updateAdSlotLive failed', e);
    return null;
  }
}

export async function createAdSlotLive(
  draft: Omit<AdSlot, 'id'>,
): Promise<AdSlot> {
  const client = await getDataClient();
  const res = await client.models.AdSlot.create(draft);
  if (!res.data) throw new Error('Could not create ad slot');
  return mapAdSlot(res.data as Record<string, unknown>);
}

export async function listSponsoredListingsLive(): Promise<SponsoredListing[]> {
  try {
    const client = await getDataClient();
    const res = await client.models.SponsoredListing.list();
    return ((res.data ?? []) as Record<string, unknown>[])
      .map(mapSponsored)
      .sort((a, b) => b.startsAt.localeCompare(a.startsAt));
  } catch (e) {
    console.error('[ShopGND] listSponsoredListingsLive failed', e);
    return [];
  }
}

export async function createSponsoredListingLive(input: {
  slotId: string;
  vendorId: string;
  productId?: string;
  classifiedId?: string;
  amountPaidCents: number;
  days?: number;
}): Promise<SponsoredListing> {
  const startsAt = new Date();
  const endsAt = new Date(startsAt);
  endsAt.setDate(endsAt.getDate() + (input.days ?? 14));

  const payload = {
    slotId: input.slotId,
    vendorId: input.vendorId,
    productId: input.productId,
    classifiedId: input.classifiedId,
    startsAt: startsAt.toISOString(),
    endsAt: endsAt.toISOString(),
    amountPaidCents: input.amountPaidCents,
  };

  const client = await getDataClient();
  const res = await client.models.SponsoredListing.create(payload);
  if (!res.data) throw new Error('Could not create sponsored listing');
  return mapSponsored(res.data as Record<string, unknown>);
}

export async function expireSponsoredListingLive(id: string): Promise<SponsoredListing | null> {
  try {
    const client = await getDataClient();
    const res = await client.models.SponsoredListing.update({ id, endsAt: nowIso() });
    return res.data ? mapSponsored(res.data as Record<string, unknown>) : null;
  } catch (e) {
    console.error('[ShopGND] expireSponsoredListingLive failed', e);
    return null;
  }
}
